import logoPecaAi from '@/assets/images/logo-peca-ai.svg';
import { usePecaAiOrderDetail } from '@/core/domain/pecaAi/orders/orders.hooks';
import { Card, Descriptions, Image, Modal, Table } from 'antd';
import { usePecaAiContext } from '../PecaAiProvider';
import StatusTag from '@/components/_common/StatusTag';
import {
  PecaAiOrderShippingStatus,
  PecaAiOrderShippingStatusColor,
  PecaAiOrderShippingStatusLabel,
  PecaAiOrderStatus,
  PecaAiOrderStatusColor,
  PecaAiOrderStatusLabel,
} from '@/core/domain/pecaAi/orders/orders.types';
import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import formatterPrice from '@/utils/number/formatterPrice';
import { Group } from '@/components/_common';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import moment from 'moment';
import formatterNumber from '@/utils/number/formatterNumber';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { decimalToTime } from '@/utils/functions/decimalToTime';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
}

export default function PecaAiOrderModal(props: Props) {
  const { cnpj, orderId } = usePecaAiContext();

  const orderDetail = usePecaAiOrderDetail(
    { document: cnpj, orderId },
    props.opened,
  );

  const order = orderDetail.data;

  const handleClose = () => {
    orderDetail.remove();
    props.onClose();
  };

  return (
    <Modal
      open={props.opened}
      onCancel={handleClose}
      onOk={() => orderDetail.refetch()}
      width={900}
      title={
        <Group gap={10}>
          <Image src={logoPecaAi} preview={false} width={60} />

          <span>Pedido de compra</span>
        </Group>
      }
      cancelText="Fechar"
      okText="Atualizar"
      style={{ position: 'relative', overflow: 'hidden', borderRadius: 8 }}
    >
      {orderDetail.isFetching && (
        <ModalLoadingOverlay posAbsolute messages={['Carregando dados...']} />
      )}

      <Descriptions
        layout="vertical"
        column={2}
        labelStyle={{ fontWeight: 600 }}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item style={{ padding: 0 }} label="Nº Pedido">
          {orderDetail.data?.orderNumber}
        </Descriptions.Item>

        <Descriptions.Item style={{ padding: 0 }} label="Status">
          <StatusTag
            label={PecaAiOrderStatusLabel[order?.status as PecaAiOrderStatus]}
            color={PecaAiOrderStatusColor[order?.status as PecaAiOrderStatus]}
          />
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        layout="vertical"
        column={4}
        labelStyle={{ fontWeight: 600 }}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item style={{ padding: 0 }} label="Prazo de entrega">
          {order?.deadlineHours ? decimalToTime(order?.deadlineHours) : '-'}
        </Descriptions.Item>

        <Descriptions.Item style={{ padding: 0 }} label="Frete">
          {formatterPrice(order?.shippingAmount || 0)}
        </Descriptions.Item>

        <Descriptions.Item style={{ padding: 0 }} label="Desconto">
          {formatterPrice(order?.discountAmount || 0)}
        </Descriptions.Item>

        <Descriptions.Item style={{ padding: 0 }} label="Valor final">
          {formatterPrice(order?.finalPrice || 0)}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        layout="vertical"
        column={3}
        labelStyle={{ fontWeight: 600 }}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item style={{ padding: 0 }} label="Endereço de entrega">
          {`${order?.shippingAddress.street}, ${
            order?.shippingAddress.number
          }, ${order?.shippingAddress.neighborhood} ${
            !!order?.shippingAddress.complement
              ? `, ${order?.shippingAddress.complement}`
              : ''
          }, ${order?.shippingAddress.city} - ${
            order?.shippingAddress.state
          } - ${applyMask(order?.shippingAddress.zipCode, cepMask)}`}
        </Descriptions.Item>
      </Descriptions>

      <strong
        style={{
          fontSize: '1.1rem',
          display: 'block',
        }}
      >
        Formas de pagamento
      </strong>

      {order?.payments.map((payment, index) => (
        <Card key={index} style={{ borderRadius: 8, marginTop: 16 }}>
          <strong style={{ fontWeight: 600, fontSize: '1.05rem' }}>
            {payment.paymentMethodName}
          </strong>

          <Descriptions column={2} labelStyle={{ fontWeight: 600 }}>
            <Descriptions.Item style={{ padding: 0 }} label="Parcelas">
              {`${payment.installments}x`}
            </Descriptions.Item>

            <Descriptions.Item style={{ padding: 0 }} label="Valor">
              {formatterPrice(payment.amount)}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ))}

      <strong
        style={{
          fontSize: '1.1rem',
          display: 'block',
          marginTop: 24,
        }}
      >
        Produtos para entrega
      </strong>

      {order?.shippings.map((shipping, index) => (
        <Card key={index} style={{ borderRadius: 8, marginTop: 16 }}>
          <Descriptions
            column={4}
            layout="vertical"
            labelStyle={{ fontWeight: 600 }}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item
              style={{ padding: 0 }}
              label="Estimativa entrega"
            >
              {moment(shipping.expectedDelivery).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>

            <Descriptions.Item style={{ padding: 0 }} label="Status">
              <StatusTag
                label={
                  PecaAiOrderShippingStatusLabel[
                    shipping?.status as PecaAiOrderShippingStatus
                  ]
                }
                color={
                  PecaAiOrderShippingStatusColor[
                    shipping?.status as PecaAiOrderShippingStatus
                  ]
                }
              />
            </Descriptions.Item>

            <Descriptions.Item style={{ padding: 0 }} label="Frete">
              {formatterPrice(shipping.amount)}
            </Descriptions.Item>

            <Descriptions.Item style={{ padding: 0 }} label="Cód. rastreio">
              {shipping.trackingCode || '-'}
            </Descriptions.Item>
          </Descriptions>

          {!!shipping.products.length && (
            <Table
              scroll={{ x: true }}
              pagination={false}
              dataSource={shipping.products}
              columns={[
                {
                  width: 50,
                  render: (_, item) => (
                    <Image
                      src={item.imageUrl}
                      preview={false}
                      width={40}
                      height={40}
                    />
                  ),
                },
                {
                  title: 'Descrição',
                  width: 200,
                  render: (_, item) => (
                    <EllipsisHoverToggle style={{ width: 200 }}>
                      {item.description}
                    </EllipsisHoverToggle>
                  ),
                },
                {
                  title: 'Qtde.',
                  dataIndex: 'quantity',
                  width: 150,
                  align: 'right',
                  render: (value) => formatterNumber(value),
                },
                {
                  title: 'Vl. unit.',
                  dataIndex: 'unitPrice',
                  width: 150,
                  align: 'right',
                  render: (value) => formatterPrice(value),
                },
                {
                  title: 'Desconto',
                  dataIndex: 'totalDiscountAmount',
                  width: 150,
                  align: 'right',
                  render: (value) => formatterPrice(value),
                },
                {
                  title: 'Vl. total',
                  dataIndex: 'totalPrice',
                  width: 150,
                  align: 'right',
                  render: (value) => formatterPrice(value),
                },
                {
                  title: 'Vl. final',
                  dataIndex: 'finalPrice',
                  width: 150,
                  align: 'right',
                  render: (value) => formatterPrice(value),
                },
              ]}
            />
          )}
        </Card>
      ))}
    </Modal>
  );
}
